import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getTransactionsCount,
  getTransactionsPage,
} from "../../../services/clearing/transactions";
import {
  Transaction,
  TransactionOrder,
  TransactionSearch,
  TransactionSearchKind,
} from "../../../types/transactions";
import { formatDateToYYYY_MM_DD } from "../../../utils/formats";
import { toSearch } from "../utils/transactionSearch";

export type ChargeType = "all" | "paid" | "free";

export type DateRange = { start: Date; end: Date };

/** Everything a page request depends on. Any change reloads from page 1. */
type Query = {
  range: DateRange;
  chargeType: ChargeType;
  order: TransactionOrder;
  search: TransactionSearch;
};

// Default window shown on first load: the last month up to today. We pre-fill
// this explicitly (rather than letting the backend infer it) so the date picker
// always reflects the range actually being queried — making it obvious why a
// payment outside that window isn't listed.
const lastMonthRange = (): DateRange => {
  const end = new Date();
  // Step back one month without month-overflow: subtracting a month from a
  // day-31 date (e.g. Mar 31) would otherwise roll into the next month (Mar 3),
  // shrinking the window. Pin the day to 1 first, then clamp it to the target
  // month's real length.
  const start = new Date(end);
  start.setDate(1);
  start.setMonth(start.getMonth() - 1);
  const daysInStartMonth = new Date(
    start.getFullYear(),
    start.getMonth() + 1,
    0
  ).getDate();
  start.setDate(Math.min(end.getDate(), daysInStartMonth));
  return { start, end };
};

type Page = {
  /** Cursor the load of this page started from (null = first page). */
  startCursor: string | null;
  items: Transaction[];
  /** Cursor to continue from, or null when this is the last page. */
  nextCursor: string | null;
};

export const PAGE_LIMIT = 100;

// Pages are full since CLR-278 (exactly `limit` items unless nextCursor is null),
// but an empty page that still exposes a nextCursor is followed as a safety net,
// bounded so a misbehaving cursor can never loop forever.
const MAX_EMPTY_PAGES = 50;

/** What Retry re-runs: the request that failed. */
type FailedAction = "reload" | "next" | null;

const requestParams = (q: Query, cursor: string | null) =>
  q.search?.kind === "cdr"
    ? { cdrId: q.search.value, limit: PAGE_LIMIT }
    : {
        dateFrom: formatDateToYYYY_MM_DD(q.range.start),
        dateTo: formatDateToYYYY_MM_DD(q.range.end),
        chargeType: q.chargeType,
        userId: q.search?.kind === "user" ? q.search.value : undefined,
        order: q.order,
        cursor: cursor ?? undefined,
        limit: PAGE_LIMIT,
      };

/**
 * The count depends on range, type and user search only — not on order or paging — and is
 * not requested for a CDR lookup. Serialised so the effect only re-runs when it changes.
 */
const countKeyOf = (q: Query): string | null =>
  q.search?.kind === "cdr"
    ? null
    : JSON.stringify({
        dateFrom: formatDateToYYYY_MM_DD(q.range.start),
        dateTo: formatDateToYYYY_MM_DD(q.range.end),
        chargeType: q.chargeType,
        userId: q.search?.kind === "user" ? q.search.value : undefined,
      });

export const useTransactionsExplorer = () => {
  const [pages, setPages] = useState<Page[]>([]);
  const [pageIndex, setPageIndex] = useState(0);
  // Starts true: the first request is sent on mount, so the table shows skeleton rows
  // instead of flashing "No payments found".
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Computed once so the default window is stable across renders.
  const [defaultRange] = useState<DateRange>(lastMonthRange);
  const [query, setQuery] = useState<Query>(() => ({
    range: defaultRange,
    chargeType: "all",
    order: "desc",
    search: null,
  }));

  const [total, setTotal] = useState<number | null>(null);
  const [totalLoading, setTotalLoading] = useState(false);
  const [totalError, setTotalError] = useState(false);

  // Monotonic ids so responses from a superseded request (filter change, rapid
  // navigation) are dropped instead of overwriting fresher state. The count has its
  // own, so it can never interfere with the table.
  const requestIdRef = useRef(0);
  const countRequestIdRef = useRef(0);
  const failedActionRef = useRef<FailedAction>(null);

  const currentPage: Page | undefined = pages[pageIndex];

  // Load a page starting at `startCursor`, skipping sparse empty pages.
  const loadFrom = useCallback(
    async (startCursor: string | null, q: Query): Promise<Page> => {
      let cursor = startCursor;

      for (let i = 0; i < MAX_EMPTY_PAGES; i++) {
        const { items, nextCursor } = await getTransactionsPage(requestParams(q, cursor));

        if (items.length > 0 || nextCursor == null) {
          return { startCursor, items, nextCursor };
        }
        cursor = nextCursor;
      }

      // Exhausted the empty-page budget — stop here rather than chase forever.
      return { startCursor, items: [], nextCursor: null };
    },
    []
  );

  // Discard the cursor stack and load page 1 for the given query. The stack is cleared
  // before the request, so a failure never leaves rows (or cursors) of the previous
  // query behind — cursors are only valid for the order they were issued with.
  const reload = useCallback(
    async (q: Query) => {
      const reqId = ++requestIdRef.current;
      failedActionRef.current = null;
      setPages([]);
      setPageIndex(0);
      setLoading(true);
      setError(false);
      try {
        const first = await loadFrom(null, q);
        if (reqId !== requestIdRef.current) return;
        setPages([first]);
      } catch (e) {
        if (reqId !== requestIdRef.current) return;
        console.error("Failed to load transactions.", e);
        failedActionRef.current = "reload";
        setError(true);
      } finally {
        if (reqId === requestIdRef.current) setLoading(false);
      }
    },
    [loadFrom]
  );

  const updateQuery = useCallback(
    (patch: Partial<Query>) => {
      const next = { ...query, ...patch };
      setQuery(next);
      reload(next);
    },
    [query, reload]
  );

  // Initial load (last month, all charge types, newest first).
  useEffect(() => {
    reload(query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The total, fetched independently of the table: it never sets `loading`/`error`.
  const countKey = useMemo(() => countKeyOf(query), [query]);
  useEffect(() => {
    const reqId = ++countRequestIdRef.current;
    setTotal(null);
    setTotalError(false);
    if (countKey == null) {
      setTotalLoading(false);
      return;
    }
    setTotalLoading(true);
    getTransactionsCount(JSON.parse(countKey))
      .then((count: number) => {
        if (reqId === countRequestIdRef.current) setTotal(count);
      })
      .catch((e: unknown) => {
        if (reqId !== countRequestIdRef.current) return;
        console.error("Failed to count transactions.", e);
        setTotalError(true);
      })
      .finally(() => {
        if (reqId === countRequestIdRef.current) setTotalLoading(false);
      });
  }, [countKey]);

  const goNext = useCallback(async () => {
    if (!currentPage) return;

    // Already visited this direction — reuse the cached page.
    if (pageIndex < pages.length - 1) {
      setPageIndex(pageIndex + 1);
      return;
    }
    if (currentPage.nextCursor == null) return;

    const reqId = ++requestIdRef.current;
    failedActionRef.current = null;
    setLoading(true);
    setError(false);
    try {
      const next = await loadFrom(currentPage.nextCursor, query);
      if (reqId !== requestIdRef.current) return;
      setPages((prev) => [...prev.slice(0, pageIndex + 1), next]);
      setPageIndex((idx) => idx + 1);
    } catch (e) {
      if (reqId !== requestIdRef.current) return;
      console.error("Failed to load the next page of transactions.", e);
      failedActionRef.current = "next";
      setError(true);
    } finally {
      if (reqId === requestIdRef.current) setLoading(false);
    }
  }, [currentPage, pageIndex, pages.length, query, loadFrom]);

  const goPrev = useCallback(() => {
    // Previous pages are cached, so this is instant and can't fail. Going back from a
    // failed Next shows the cached page again.
    failedActionRef.current = null;
    setError(false);
    setPageIndex((idx) => (idx > 0 ? idx - 1 : idx));
  }, []);

  // Re-fetch the page currently on screen.
  const reloadCurrentPage = useCallback(async () => {
    if (!currentPage) {
      await reload(query);
      return;
    }
    const reqId = ++requestIdRef.current;
    setLoading(true);
    setError(false);
    try {
      const page = await loadFrom(currentPage.startCursor, query);
      if (reqId !== requestIdRef.current) return;
      setPages((prev) => {
        const copy = prev.slice(0, pageIndex + 1);
        copy[pageIndex] = page;
        return copy;
      });
    } catch (e) {
      if (reqId !== requestIdRef.current) return;
      console.error("Failed to reload transactions.", e);
      setError(true);
    } finally {
      if (reqId === requestIdRef.current) setLoading(false);
    }
  }, [currentPage, pageIndex, query, loadFrom, reload]);

  // Re-run whatever failed: page 1 of the current query, or the same Next request.
  const retry = useCallback(async () => {
    const failed = failedActionRef.current;
    if (failed === "reload") return reload(query);
    if (failed === "next") return goNext();
    return reloadCurrentPage();
  }, [query, reload, goNext, reloadCurrentPage]);

  const applyPeriod = useCallback(
    ({ start, end }: DateRange) => updateQuery({ range: { start, end } }),
    [updateQuery]
  );

  // Reset the window back to the default (last month) shown on first load. A fresh
  // object, so the picker re-syncs even when the default was already applied.
  const clearPeriod = useCallback(
    () => updateQuery({ range: { start: defaultRange.start, end: defaultRange.end } }),
    [defaultRange, updateQuery]
  );

  const setChargeType = useCallback(
    (nextChargeType: ChargeType) => {
      if (nextChargeType === query.chargeType) return;
      updateQuery({ chargeType: nextChargeType });
    },
    [query.chargeType, updateQuery]
  );

  const setOrder = useCallback(
    (nextOrder: TransactionOrder) => {
      if (nextOrder === query.order) return;
      updateQuery({ order: nextOrder });
    },
    [query.order, updateQuery]
  );

  /** Server-side search: UUID → CDR lookup, else user ID; `kind` forces one. Blank clears. */
  const applySearch = useCallback(
    (raw: string, kind?: TransactionSearchKind) => {
      const nextSearch = toSearch(raw, kind);
      if (nextSearch == null && query.search == null) return;
      updateQuery({ search: nextSearch });
    },
    [query.search, updateQuery]
  );

  const hasPrev = pageIndex > 0;
  const hasNext =
    pageIndex < pages.length - 1 || (currentPage?.nextCursor ?? null) !== null;

  return {
    items: currentPage?.items ?? [],
    loading,
    error,
    chargeType: query.chargeType,
    dateRange: query.range,
    order: query.order,
    search: query.search,
    pageNumber: pageIndex + 1,
    pageSize: PAGE_LIMIT,
    hasPrev,
    hasNext,
    total,
    totalLoading,
    totalError,
    goNext,
    goPrev,
    retry,
    applyPeriod,
    clearPeriod,
    setChargeType,
    setOrder,
    applySearch,
  };
};
